// frontend/src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token with server
      const response = await api.get('/auth/verify');
      if (response.success) {
        setUser(response.user);
        // Refresh token if needed
        if (response.newToken) {
          localStorage.setItem('token', response.newToken);
        }
      } else {
        localStorage.removeItem('token');
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      // const response = await api.post('/auth/login', { email, password });
      const response = await api.auth.login({ email, password });
      console.log(response.data);
      if (response.success) {
        localStorage.setItem('token', response.token);
        setUser(response.user);
        return { success: true, data: response };
      } else {
        setError(response.message || 'Login failed');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMsg = err.message || 'An error occurred during login';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await api.post('/auth/register', userData);
      
      if (response.success) {
        localStorage.setItem('token', response.token);
        setUser(response.user);
        return { success: true, data: response };
      } else {
        setError(response.message || 'Registration failed');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMsg = err.message || 'An error occurred during registration';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setError(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      if (response.success && response.user) {
        setUser(prev => ({ ...prev, ...response.user }));
      }
      return response;
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isCoach: user?.userType === 'coach',
    isAdmin: user?.userType === 'admin',
    login,
    register,
    logout,
    updateProfile,
    checkAuth,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Higher Order Component for protected routes
export const withAuth = (Component) => {
  return function WithAuthComponent(props) {
    const { user, loading } = useAuth();
    
    if (loading) {
      return <div className="loading-spinner">Loading...</div>;
    }
    
    if (!user) {
      // Redirect to login or show access denied
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return null;
    }
    
    return <Component {...props} />;
  };
};

// Hook for coach-only access
export const useCoachAuth = () => {
  const auth = useAuth();
  
  useEffect(() => {
    if (auth.user && auth.user.userType !== 'coach') {
      window.location.href = '/';
    }
  }, [auth.user]);
  
  return auth;
};